import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Chip,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Pagination,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalculateIcon from '@mui/icons-material/Calculate';
import { useLocation, useNavigate } from 'react-router-dom';
import { FTA_ABSD_INFO_TOOLTIP } from '../../utils/stampDutyRates';
import { useProfile } from '../context/ProfileContext';
import CalculatorCard from '../components/CalculatorCard';
import InfoTooltip from '../components/InfoTooltip';
import { getHaircutInfo, calculateAssessableIncome } from '../utils/haircutFormulas';
import type { CalculatorProfile, CalculatorProfileGroup, CreateProfileRequest, CreateGroupRequest } from '../services/profileService';
import { listExampleHistory } from '../services/exampleHistoryService';
import { requestPendingHistoryLoad } from '../context/LinkedCalculatorContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface ProfileManagerProps {
  /** Omit outer CalculatorCard (e.g. when shown inside a dialog that supplies its own title). */
  noCard?: boolean;
  /** When the host opens Profile Manager to edit a specific profile (e.g. pencil in header menu). */
  initialEditProfileId?: number | null;
  /** Called after the edit dialog is opened from `initialEditProfileId` so the host can clear it. */
  onInitialEditConsumed?: () => void;
  /** When the host opens Profile Manager to edit a specific group (e.g. pencil in header menu). */
  initialEditGroupId?: number | null;
  /** Called after the group edit dialog is opened from `initialEditGroupId` so the host can clear it. */
  onInitialEditGroupConsumed?: () => void;
  /** Called after a saved-calculator button navigates away (e.g. host dialog closes itself). */
  onNavigateToCalculator?: () => void;
}

/** Linked calculators that tag a history entry with savedFromCalculator. */
type SavedCalcKey = 'buying' | 'maximizer' | 'decoupling';

/** Newest saved history entry id per calculator, for one profile or group. */
type SavedCalcLinks = Partial<Record<SavedCalcKey, string>>;

const SAVED_CALC_ROUTE: Record<SavedCalcKey, string> = {
  buying: 'buying-calculator',
  maximizer: 'affordability',
  decoupling: 'decoupling-calculator',
};

const ProfileManager: React.FC<ProfileManagerProps> = ({
  noCard = false,
  initialEditProfileId = null,
  onInitialEditConsumed,
  initialEditGroupId = null,
  onInitialEditGroupConsumed,
  onNavigateToCalculator,
}) => {
  const {
    profiles,
    groups,
    selectedProfile,
    isLoading,
    refreshProfiles,
    addProfile,
    updateProfile,
    deleteProfile,
    addGroup,
    updateGroup,
    deleteGroup,
    selectProfile,
    selectGroup,
    getSelectedName,
  } = useProfile();

  const [tabValue, setTabValue] = useState(0);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isGroupDialogOpen, setIsGroupDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<CalculatorProfile | null>(null);
  const [editingGroup, setEditingGroup] = useState<CalculatorProfileGroup | null>(null);
  
  // Profile form state
  const [profileName, setProfileName] = useState('');
  const [age, setAge] = useState<string>('');
  const [monthlyIncome, setMonthlyIncome] = useState<string>('');
  const [selfEmployedInd, setSelfEmployedInd] = useState(false);
  const [otherAnnualIncome, setOtherAnnualIncome] = useState<string>('');
  const [saving, setSaving] = useState<string>('');
  const [cpf, setCpf] = useState<string>('');
  const [carLoan, setCarLoan] = useState<string>('');
  const [housingLoan, setHousingLoan] = useState<string>('');
  const [creditCard, setCreditCard] = useState<string>('');
  const [otherLoan, setOtherLoan] = useState<string>('');
  const [profileRemarks, setProfileRemarks] = useState('');
  const [citizenship, setCitizenship] = useState<string>('SC');
  const [ftaNationalityEligible, setFtaNationalityEligible] = useState(false);
  const [propertiesOwned, setPropertiesOwned] = useState<1 | 2 | 3>(1);

  // Calculate birth year from age
  const getBirthYearFromAge = (ageValue: string): number | null => {
    if (!ageValue || ageValue.trim() === '') return null;
    const ageNum = parseInt(ageValue);
    if (isNaN(ageNum) || ageNum < 0 || ageNum > 150) return null;
    const currentYear = new Date().getFullYear();
    return currentYear - ageNum;
  };
  
  // Calculate age from birth year
  const getAgeFromBirthYear = (birthYear: number | null | undefined): string => {
    if (!birthYear) return '';
    const currentYear = new Date().getFullYear();
    const calculatedAge = currentYear - birthYear;
    return calculatedAge > 0 && calculatedAge <= 150 ? String(calculatedAge) : '';
  };
  
  // Group form state
  const [groupName, setGroupName] = useState('');
  const [groupRemarks, setGroupRemarks] = useState('');
  const [groupRelationship, setGroupRelationship] = useState<string>('INDIVIDUAL');
  const [selectedProfileIds, setSelectedProfileIds] = useState<number[]>([]);
  const [creatingProfileInGroup, setCreatingProfileInGroup] = useState(false);
  const [newProfileInGroup, setNewProfileInGroup] = useState<CreateProfileRequest | null>(null);
  const [profileSearchTerm, setProfileSearchTerm] = useState('');
  const [profilePage, setProfilePage] = useState(1);
  const profilesPerPage = 10;
  // Search state for individual profiles
  const [individualProfileSearchTerm, setIndividualProfileSearchTerm] = useState('');
  // Search state for profile groups
  const [groupSearchTerm, setGroupSearchTerm] = useState('');

  useEffect(() => {
    refreshProfiles();
  }, [refreshProfiles]);

  const formatCurrency = (value: string | number | null | undefined): string => {
    if (!value) return '';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatNumber = (value: string): string => {
    return value.replace(/\D/g, '');
  };

  const formatNumberWithCommas = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const parseNumberString = (value: string): number | null => {
    const digits = value.replace(/,/g, '').trim();
    if (!digits) return null;
    const num = parseInt(digits, 10);
    return Number.isNaN(num) ? null : num;
  };

  // Profile handlers
  const handleCreateProfile = () => {
    setEditingProfile(null);
    setProfileName('');
    setAge('');
    setMonthlyIncome('');
    setSelfEmployedInd(false);
    setOtherAnnualIncome('');
    setSaving('');
    setCpf('');
    setCarLoan('');
    setHousingLoan('');
    setCreditCard('');
    setOtherLoan('');
    setProfileRemarks('');
    setCitizenship('SC');
    setFtaNationalityEligible(false);
    setPropertiesOwned(1);
    setIsProfileDialogOpen(true);
  };

  const handleEditProfileRef = useRef<(profile: CalculatorProfile) => void>(() => {});

  const handleEditProfile = (profile: CalculatorProfile) => {
    setEditingProfile(profile);
    setProfileName(profile.Name);
    setAge(getAgeFromBirthYear(profile.BirthYear));
    setMonthlyIncome(profile.MonthlyIncome ? formatNumberWithCommas(String(profile.MonthlyIncome)) : '');
    setSelfEmployedInd(profile.SelfEmployedInd === 1);
    setOtherAnnualIncome(profile.OtherAnnualIncome ? formatNumberWithCommas(String(profile.OtherAnnualIncome)) : '');
    setSaving(profile.Saving != null ? formatNumberWithCommas(String(profile.Saving)) : '');
    setCpf(profile.Cpf != null ? formatNumberWithCommas(String(profile.Cpf)) : '');
    setCarLoan(profile.CarLoan != null ? formatNumberWithCommas(String(profile.CarLoan)) : '');
    setHousingLoan(profile.HousingLoan != null ? formatNumberWithCommas(String(profile.HousingLoan)) : '');
    setCreditCard(profile.CreditCard != null ? formatNumberWithCommas(String(profile.CreditCard)) : '');
    setOtherLoan(profile.OtherLoan != null ? formatNumberWithCommas(String(profile.OtherLoan)) : '');
    setProfileRemarks(profile.Remarks || '');
    setCitizenship(profile.Citizenship || 'SC');
    setFtaNationalityEligible(Number(profile.FtaNationalityInd) === 1);
    {
      const po = profile.PropertiesOwned;
      setPropertiesOwned(po === 2 ? 2 : po === 3 ? 3 : 1);
    }
    setIsProfileDialogOpen(true);
  };

  handleEditProfileRef.current = handleEditProfile;

  useEffect(() => {
    if (initialEditProfileId == null) return;
    const profile = profiles.find((p) => p.Id === initialEditProfileId);
    if (!profile) return;
    handleEditProfileRef.current(profile);
    setTabValue(0);
    onInitialEditConsumed?.();
  }, [initialEditProfileId, profiles, onInitialEditConsumed]);

  const handleSaveProfile = async () => {
    if (!profileName.trim()) {
      alert('Please enter a profile name');
      return;
    }

    try {
      const birthYear = getBirthYearFromAge(age);
      const data: CreateProfileRequest = {
        Name: profileName.trim(),
        BirthYear: birthYear,
        MonthlyIncome: parseNumberString(monthlyIncome),
        OtherAnnualIncome: parseNumberString(otherAnnualIncome),
        Saving: parseNumberString(saving),
        Cpf: parseNumberString(cpf),
        CarLoan: parseNumberString(carLoan),
        HousingLoan: parseNumberString(housingLoan),
        CreditCard: parseNumberString(creditCard),
        OtherLoan: parseNumberString(otherLoan),
        SelfEmployedInd: selfEmployedInd ? 1 : 0,
        Citizenship: citizenship,
        FtaNationalityInd: citizenship === 'SC' ? 0 : (ftaNationalityEligible ? 1 : 0),
        PropertiesOwned: propertiesOwned,
        Remarks: profileRemarks.trim() || null,
      };

      if (editingProfile) {
        await updateProfile(editingProfile.Id, data);
      } else {
        const newProfile = await addProfile(data);
        // Auto-select new profile
        selectProfile(newProfile.Id);
      }
      
      setIsProfileDialogOpen(false);
      setEditingProfile(null);
      await refreshProfiles();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    }
  };

  const isProfileInActiveGroup = (profileId: number): boolean =>
    groups.some((g) => g.profiles?.some((p) => p.Id === profileId));

  const handleDeleteProfile = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this profile?')) {
      try {
        await deleteProfile(id);
        await refreshProfiles();
      } catch (error) {
        console.error('Error deleting profile:', error);
        const message = error instanceof Error ? error.message : 'Failed to delete profile. Please try again.';
        alert(message);
      }
    }
  };

  // Group handlers
  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupName('');
    setGroupRelationship('INDIVIDUAL');
    setGroupRemarks('');
    setSelectedProfileIds([]);
    setCreatingProfileInGroup(false);
    setNewProfileInGroup(null);
    setProfileSearchTerm('');
    setProfilePage(1);
    setIsGroupDialogOpen(true);
  };

  const handleEditGroup = (group: CalculatorProfileGroup) => {
    setEditingGroup(group);
    setGroupName(group.Name);
    setGroupRelationship(group.Relationship || 'INDIVIDUAL');
    setGroupRemarks(group.Remarks || '');
    setSelectedProfileIds(group.profiles?.map(p => p.Id) || []);
    setCreatingProfileInGroup(false);
    setNewProfileInGroup(null);
    setProfileSearchTerm('');
    setProfilePage(1);
    setIsGroupDialogOpen(true);
  };

  const handleEditGroupRef = useRef<(group: CalculatorProfileGroup) => void>(() => {});
  handleEditGroupRef.current = handleEditGroup;

  useEffect(() => {
    if (initialEditGroupId == null) return;
    const group = groups.find((g) => g.Id === initialEditGroupId);
    if (!group) return;
    handleEditGroupRef.current(group);
    setTabValue(1);
    onInitialEditGroupConsumed?.();
  }, [initialEditGroupId, groups, onInitialEditGroupConsumed]);

  const handleSaveGroup = async () => {
    if (!groupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    try {
      let finalProfileIds = [...selectedProfileIds];
      
      // If creating a new profile within the group form
      if (creatingProfileInGroup && newProfileInGroup) {
        const newProfile = await addProfile(newProfileInGroup);
        finalProfileIds.push(newProfile.Id);
      }

      const data: CreateGroupRequest = {
        Name: groupName.trim(),
        Relationship: groupRelationship,
        Remarks: groupRemarks.trim() || null,
        ProfileIds: finalProfileIds,
      };

      if (editingGroup) {
        await updateGroup(editingGroup.Id, data);
      } else {
        const newGroup = await addGroup(data);
        // Auto-select new group
        selectGroup(newGroup.Id);
      }
      
      setIsGroupDialogOpen(false);
      setEditingGroup(null);
      setCreatingProfileInGroup(false);
      setNewProfileInGroup(null);
      await refreshProfiles();
    } catch (error) {
      console.error('Error saving group:', error);
      alert('Failed to save group. Please try again.');
    }
  };

  const handleDeleteGroup = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      try {
        await deleteGroup(id);
        await refreshProfiles();
      } catch (error) {
        console.error('Error deleting group:', error);
        alert('Failed to delete group. Please try again.');
      }
    }
  };

  const handleToggleProfileInGroup = (profileId: number) => {
    setSelectedProfileIds((prev) =>
      prev.includes(profileId)
        ? prev.filter((id) => id !== profileId)
        : [...prev, profileId]
    );
  };

  const handleCreateProfileInGroup = () => {
    setCreatingProfileInGroup(true);
    setNewProfileInGroup({
      Name: '',
      BirthYear: null,
      MonthlyIncome: null,
      OtherAnnualIncome: null,
      Saving: null,
      Cpf: null,
      CarLoan: null,
      HousingLoan: null,
      CreditCard: null,
      OtherLoan: null,
      FtaNationalityInd: 0,
      PropertiesOwned: 1,
      Remarks: null,
    });
  };

  const handleSelectProfile = (id: number) => {
    selectProfile(id);
  };

  const handleSelectGroup = (id: number) => {
    selectGroup(id);
  };

  // ── Saved calculator states linked to profiles ──────────────────────────
  // History entries saved while a profile/group was loaded carry a
  // savedProfileRef + savedFromCalculator tag. The newest entry per calculator
  // powers the per-card "Buying Calc" / "Maximizer" / "Decoupling Calc" buttons below.
  const navigate = useNavigate();
  const location = useLocation();
  const [savedCalcLinks, setSavedCalcLinks] = useState<Map<string, SavedCalcLinks>>(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listExampleHistory();
        if (cancelled) return;
        const links = new Map<string, SavedCalcLinks>();
        [...rows]
          // Newest first, so the most recent save per calculator wins.
          .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '') || b.id - a.id)
          .forEach((row) => {
            const pps = row.purchasePlannerState as {
              savedProfileRef?: { type?: string; id?: number };
              savedFromCalculator?: string;
            } | null | undefined;
            const ref = pps?.savedProfileRef;
            const calc = pps?.savedFromCalculator;
            if (!ref || typeof ref.id !== 'number' || (ref.type !== 'profile' && ref.type !== 'group')) return;
            if (calc !== 'buying' && calc !== 'maximizer' && calc !== 'decoupling') return;
            const key = `${ref.type}-${ref.id}`;
            const existing = links.get(key) ?? {};
            if (!existing[calc]) {
              existing[calc] = `history-${row.id}`;
              links.set(key, existing);
            }
          });
        setSavedCalcLinks(links);
      } catch (error) {
        console.error('Failed to load saved calculator links:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Select the profile, queue the saved snapshot for the LinkedCalculatorProvider
  // to apply, then navigate to the calculator.
  const openSavedCalculator = (
    refType: 'profile' | 'group',
    refId: number,
    calc: SavedCalcKey,
    historyId: string,
  ) => {
    // Queue the load BEFORE (re)selecting the profile so the calculators'
    // profile-load effects see it and skip their default form population.
    requestPendingHistoryLoad(historyId);
    const alreadySelected = selectedProfile?.type === refType && selectedProfile.id === refId;
    if (!alreadySelected) {
      if (refType === 'group') selectGroup(refId); else selectProfile(refId);
    }
    const basePath = location.pathname.startsWith('/super-calculator') ? '/super-calculator' : '';
    navigate(`${basePath}/${SAVED_CALC_ROUTE[calc]}`);
    onNavigateToCalculator?.();
  };

  const renderSavedCalcButtons = (refType: 'profile' | 'group', refId: number) => {
    const links = savedCalcLinks.get(`${refType}-${refId}`);
    if (!links || (!links.buying && !links.maximizer && !links.decoupling)) return null;
    const btnSx = {
      fontSize: '0.68rem',
      py: 0.25,
      px: 1,
      borderColor: '#D97706',
      color: '#D97706',
      '&:hover': { borderColor: '#B86205', backgroundColor: 'rgba(217,119,6,0.08)' },
    } as const;
    return (
      <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
        {links.buying && (
          <Tooltip title="Load the Buying Calculator saved with this profile">
            <Button
              size="small"
              variant="outlined"
              startIcon={<CalculateIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                openSavedCalculator(refType, refId, 'buying', links.buying!);
              }}
              sx={btnSx}
            >
              Buying Calc
            </Button>
          </Tooltip>
        )}
        {links.maximizer && (
          <Tooltip title="Load the Max Affordability Calculator saved with this profile">
            <Button
              size="small"
              variant="outlined"
              startIcon={<CalculateIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                openSavedCalculator(refType, refId, 'maximizer', links.maximizer!);
              }}
              sx={btnSx}
            >
              Max Affordability Calculator
            </Button>
          </Tooltip>
        )}
        {links.decoupling && (
          <Tooltip title="Load the Decoupling Calculator saved with this profile">
            <Button
              size="small"
              variant="outlined"
              startIcon={<CalculateIcon sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                openSavedCalculator(refType, refId, 'decoupling', links.decoupling!);
              }}
              sx={btnSx}
            >
              Decoupling Calc
            </Button>
          </Tooltip>
        )}
      </Box>
    );
  };

  const wrapCard = (children: React.ReactNode) =>
    noCard ? (
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    ) : (
      <CalculatorCard
        title="Profile Manager"
        description="Manage your individual profiles and profile groups. Select a profile or group to use in calculators."
      >
        {children}
      </CalculatorCard>
    );

  if (isLoading) {
    return wrapCard(
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return wrapCard(
    <>
      {selectedProfile && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Currently selected: <strong>{getSelectedName()}</strong>
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<PersonIcon />} iconPosition="start" label="Individual Profiles" />
          <Tab icon={<GroupIcon />} iconPosition="start" label="Profile Groups" />
        </Tabs>
      </Box>

      {/* Individual Profiles Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateProfile}
            sx={{
              backgroundColor: '#D97706',
              '&:hover': {
                backgroundColor: '#B86205',
              },
            }}
          >
            Create Profile
          </Button>
          <TextField
            placeholder="Search profiles by name..."
            value={individualProfileSearchTerm}
            onChange={(e) => setIndividualProfileSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Grid container spacing={2}>
          {profiles
            .filter((profile) =>
              profile.Name.toLowerCase().includes(individualProfileSearchTerm.toLowerCase())
            )
            .map((profile) => {
              const isSelected = selectedProfile?.type === 'profile' && selectedProfile.id === profile.Id;
              const currentYear = new Date().getFullYear();
              const age = profile.BirthYear ? currentYear - profile.BirthYear : null;
              const assessableIncome = calculateAssessableIncome(
                profile.MonthlyIncome ? String(profile.MonthlyIncome) : '0',
                profile.OtherAnnualIncome ? String(profile.OtherAnnualIncome) : '0',
                profile.SelfEmployedInd
              );
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={profile.Id}>
                  <Card
                    sx={{
                      border: isSelected ? '2px solid #D97706' : '1px solid',
                      borderColor: isSelected ? '#D97706' : 'divider',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => handleSelectProfile(profile.Id)}
                  >
                    <CardHeader
                      title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            {profile.Name}
                          </Typography>
                          {isSelected && (
                            <Chip label="Selected" size="small" sx={{ backgroundColor: '#D97706', color: 'white', height: 20, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      }
                      action={
                        <Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProfile(profile);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <Tooltip
                            title={
                              isProfileInActiveGroup(profile.Id)
                                ? 'Cannot delete: profile is in an active group. Remove it from the group first.'
                                : 'Delete profile'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteProfile(profile.Id);
                                }}
                                color="error"
                                disabled={isProfileInActiveGroup(profile.Id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      }
                      sx={{ pb: 1 }}
                    />
                    <CardContent sx={{ pt: 0, pb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {age !== null ? `Age: ${age} years` : 'Age: N/A'}
                        </Typography>
                        <Chip
                          label={profile.Citizenship === 'SPR' ? 'PR' : profile.Citizenship === 'FOREIGNER' ? 'Foreigner' : 'SC'}
                          size="small"
                          sx={{ height: 18, fontSize: '0.6rem' }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          Monthly Income: {formatCurrency(profile.MonthlyIncome)}
                        </Typography>
                        {profile.SelfEmployedInd === 1 && (
                          <Chip label="Self Employed" size="small" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: '#D9770620', color: '#D97706', border: '1px solid #D97706' }} />
                        )}
                      </Box>
                      {profile.OtherAnnualIncome && (
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.8rem' }}>
                          Other Annual: {formatCurrency(profile.OtherAnnualIncome)}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'primary.main', mt: 1 }}>
                        Assessable Income: {formatCurrency(assessableIncome)}
                      </Typography>
                      {profile.Remarks && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.7rem' }}>
                          {profile.Remarks}
                        </Typography>
                      )}
                      {renderSavedCalcButtons('profile', profile.Id)}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
        </Grid>

        {profiles.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No profiles yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first profile to use in calculators
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateProfile}
              sx={{
                backgroundColor: '#D97706',
                '&:hover': {
                  backgroundColor: '#B86205',
                },
              }}
            >
              Create Profile
            </Button>
          </Paper>
        )}
      </TabPanel>

      {/* Profile Groups Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateGroup}
            sx={{
              backgroundColor: '#D97706',
              '&:hover': {
                backgroundColor: '#B86205',
              },
            }}
          >
            Create Group
          </Button>
          <TextField
            placeholder="Search groups by name..."
            value={groupSearchTerm}
            onChange={(e) => setGroupSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Grid container spacing={2}>
          {groups
            .filter((group) =>
              group.Name.toLowerCase().includes(groupSearchTerm.toLowerCase())
            )
            .map((group) => {
              const isSelected = selectedProfile?.type === 'group' && selectedProfile.id === group.Id;
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={group.Id}>
                  <Card
                    sx={{
                      border: isSelected ? '2px solid #D97706' : '1px solid',
                      borderColor: isSelected ? '#D97706' : 'divider',
                      backgroundColor: isSelected ? 'rgba(217,119,6,0.08)' : 'background.paper',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        borderColor: isSelected ? '#D97706' : 'text.disabled',
                      },
                    }}
                    onClick={() => handleSelectGroup(group.Id)}
                  >
                    <CardHeader
                      title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            {group.Name}
                          </Typography>
                          {isSelected && (
                            <Chip label="Selected" size="small" sx={{ backgroundColor: '#D97706', color: 'white', height: 20, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      }
                      subheader={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            {group.profiles?.length || 0} {group.profiles?.length === 1 ? 'Profile' : 'Profiles'}
                          </Typography>
                          {group.Relationship && (
                            <Chip
                              label={group.Relationship === 'SPOUSE' ? 'Married Couple' : 'Individual'}
                              size="small"
                              sx={{ height: 18, fontSize: '0.6rem' }}
                            />
                          )}
                        </Box>
                      }
                      action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                          <Tooltip title="Edit group">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditGroup(group);
                              }}
                              color="default"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete group">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteGroup(group.Id);
                              }}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      sx={{ pb: 0.5 }}
                    />
                    <CardContent sx={{ pt: 0, pb: 1.5 }}>
                      {group.profiles && group.profiles.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                          {group.profiles.map((profile) => {
                            const currentYear = new Date().getFullYear();
                            const age = profile.BirthYear ? currentYear - profile.BirthYear : null;
                            const assessableIncome = calculateAssessableIncome(
                              profile.MonthlyIncome ? String(profile.MonthlyIncome) : '0',
                              profile.OtherAnnualIncome ? String(profile.OtherAnnualIncome) : '0',
                              profile.SelfEmployedInd
                            );

                            return (
                              <Box
                                key={profile.Id}
                                sx={{
                                  p: 1.25,
                                  backgroundColor: 'action.hover',
                                  borderRadius: 1.5,
                                  borderLeft: '3px solid #D97706',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PersonIcon sx={{ fontSize: 14, color: '#D97706' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                      {profile.Name}
                                    </Typography>
                                  </Box>
                                  {age !== null && (
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                      Age {age}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                      Monthly: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{formatCurrency(profile.MonthlyIncome)}</Box>
                                    </Typography>
                                    {profile.OtherAnnualIncome ? (
                                      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                        Other: <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{formatCurrency(profile.OtherAnnualIncome)}</Box>
                                      </Typography>
                                    ) : null}
                                  </Box>
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#D97706', fontWeight: 600 }}>
                                    {formatCurrency(assessableIncome)}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      {group.Remarks && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                          {group.Remarks}
                        </Typography>
                      )}
                      {renderSavedCalcButtons('group', group.Id)}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
        </Grid>

        {groups.filter((group) =>
          group.Name.toLowerCase().includes(groupSearchTerm.toLowerCase())
        ).length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {groupSearchTerm ? 'No groups found' : 'No groups yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {groupSearchTerm 
                ? `No groups match "${groupSearchTerm}"`
                : 'Create your first profile group to combine multiple profiles'}
            </Typography>
            {!groupSearchTerm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateGroup}
                sx={{
                  backgroundColor: '#D97706',
                  '&:hover': {
                    backgroundColor: '#B86205',
                  },
                }}
              >
                Create Group
              </Button>
            )}
          </Paper>
        )}
      </TabPanel>

      {/* Profile Dialog */}
      <Dialog open={isProfileDialogOpen} onClose={() => setIsProfileDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingProfile ? 'Edit Profile' : 'Create Profile'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Profile
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Profile Name"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Age"
                    value={age}
                    onChange={(e) => setAge(formatNumber(e.target.value))}
                    type="number"
                    InputProps={{
                      endAdornment: (
                        <InfoTooltip
                          title={getHaircutInfo('age')?.field || 'Age'}
                          description={getHaircutInfo('age')?.description}
                          formula={getHaircutInfo('age')?.formula}
                          source={getHaircutInfo('age')?.source}
                        />
                      ),
                    }}
                    helperText={
                      age && !isNaN(parseInt(age)) && parseInt(age) >= 0 && parseInt(age) <= 150
                        ? `Birth Year: ${getBirthYearFromAge(age)}`
                        : ''
                    }
                    FormHelperTextProps={{
                      sx: { color: 'error.main' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Citizenship</InputLabel>
                    <Select
                      value={citizenship}
                      label="Citizenship"
                      onChange={(e) => {
                        const v = e.target.value;
                        setCitizenship(v);
                        if (v === 'SC') setFtaNationalityEligible(false);
                      }}
                    >
                      <MenuItem value="SC">Singapore Citizen</MenuItem>
                      <MenuItem value="SPR">Singapore PR</MenuItem>
                      <MenuItem value="FOREIGNER">Foreigner</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {(citizenship === 'SPR' || citizenship === 'FOREIGNER') && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          FTA nationality (eligible for SC-equivalent ABSD)?
                        </Typography>
                        <Tooltip
                          title={
                            <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 360, whiteSpace: 'pre-line' }}>
                              {FTA_ABSD_INFO_TOOLTIP}
                            </Typography>
                          }
                          arrow
                          placement="top"
                          enterTouchDelay={0}
                        >
                          <IconButton size="small" aria-label="FTA nationality requirements" sx={{ p: 0.25 }}>
                            <InfoOutlinedIcon fontSize="small" sx={{ color: '#D97706' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <RadioGroup
                        row
                        value={ftaNationalityEligible ? 'yes' : 'no'}
                        onChange={(e) => setFtaNationalityEligible(e.target.value === 'yes')}
                        sx={{ gap: 0.5 }}
                      >
                        <FormControlLabel
                          value="no"
                          control={<Radio size="small" sx={{ color: '#D97706', '&.Mui-checked': { color: '#D97706' } }} />}
                          label={<Typography variant="body2">No</Typography>}
                        />
                        <FormControlLabel
                          value="yes"
                          control={<Radio size="small" sx={{ color: '#D97706', '&.Mui-checked': { color: '#D97706' } }} />}
                          label={<Typography variant="body2">Yes</Typography>}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    Properties owned
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 2,
                      py: 1.25,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <RadioGroup
                      row
                      value={String(propertiesOwned)}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        if (v === 1 || v === 2 || v === 3) setPropertiesOwned(v);
                      }}
                      sx={{ gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap' }}
                    >
                      <FormControlLabel
                        value="1"
                        control={<Radio size="small" sx={{ color: '#D97706', '&.Mui-checked': { color: '#D97706' } }} />}
                        label={<Typography variant="body2">1st Property</Typography>}
                      />
                      <FormControlLabel
                        value="2"
                        control={<Radio size="small" sx={{ color: '#D97706', '&.Mui-checked': { color: '#D97706' } }} />}
                        label={<Typography variant="body2">2nd Property</Typography>}
                      />
                      <FormControlLabel
                        value="3"
                        control={<Radio size="small" sx={{ color: '#D97706', '&.Mui-checked': { color: '#D97706' } }} />}
                        label={<Typography variant="body2">≥ 3rd Property</Typography>}
                      />
                    </RadioGroup>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Income
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monthly Income"
                    value={monthlyIncome}
                    onChange={(e) => setMonthlyIncome(formatNumberWithCommas(e.target.value))}
                    type="text"
                    inputMode="numeric"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.75rem' }}>$</Typography>,
                      endAdornment: (
                        <InfoTooltip
                          title={getHaircutInfo('monthlyIncome')?.field || 'Monthly Income'}
                          description={getHaircutInfo('monthlyIncome')?.description}
                          formula={getHaircutInfo('monthlyIncome')?.formula}
                          source={getHaircutInfo('monthlyIncome')?.source}
                        />
                      ),
                    }}
                    helperText={
                      selfEmployedInd
                        ? '70% recognition (30% haircut — Self Employed)'
                        : `${getHaircutInfo('monthlyIncome')?.recognitionPercent || 100}% recognition (no haircut)`
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selfEmployedInd}
                        onChange={(e) => setSelfEmployedInd(e.target.checked)}
                        size="small"
                        sx={{
                          color: '#D97706',
                          '&.Mui-checked': { color: '#D97706' },
                          py: 0.5,
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ color: selfEmployedInd ? '#D97706' : 'text.secondary' }}>
                        Self Employed (30% income haircut applies)
                      </Typography>
                    }
                    sx={{ mt: 0.5, ml: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Other Annual Income"
                    value={otherAnnualIncome}
                    onChange={(e) => setOtherAnnualIncome(formatNumberWithCommas(e.target.value))}
                    type="text"
                    inputMode="numeric"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.75rem' }}>$</Typography>,
                      endAdornment: (
                        <InfoTooltip
                          title={getHaircutInfo('otherAnnualIncome')?.field || 'Other Annual Income'}
                          description={getHaircutInfo('otherAnnualIncome')?.description}
                          formula={getHaircutInfo('otherAnnualIncome')?.formula}
                          source={getHaircutInfo('otherAnnualIncome')?.source}
                        />
                      ),
                    }}
                    helperText={`Commission/Incentives/Bonus/Overtime - ${getHaircutInfo('otherAnnualIncome')?.recognitionPercent || 70}% recognition (${getHaircutInfo('otherAnnualIncome')?.haircutPercent || 30}% haircut)`}
                  />
                </Grid>
                {(monthlyIncome || otherAnnualIncome) && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 1.5, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Assessable Income (Monthly):
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(
                            calculateAssessableIncome(
                              (monthlyIncome || '0').replace(/,/g, ''),
                              (otherAnnualIncome || '0').replace(/,/g, ''),
                              selfEmployedInd ? 1 : 0
                            )
                          )}
                        </Typography>
                        <InfoTooltip
                          title={getHaircutInfo('assessableIncome')?.field || 'Assessable Income'}
                          description={getHaircutInfo('assessableIncome')?.description}
                          formula={getHaircutInfo('assessableIncome')?.formula}
                          source={getHaircutInfo('assessableIncome')?.source}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Fund Availability
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cash / Saving"
                    value={saving}
                    onChange={(e) => setSaving(formatNumberWithCommas(e.target.value))}
                    type="text"
                    inputMode="numeric"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.75rem' }}>$</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="CPF OA"
                    value={cpf}
                    onChange={(e) => setCpf(formatNumberWithCommas(e.target.value))}
                    type="text"
                    inputMode="numeric"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.75rem' }}>$</Typography>,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Monthly Debt Obligations
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Car Loan"
                    value={carLoan}
                    onChange={(e) => setCarLoan(formatNumberWithCommas(e.target.value))}
                    type="text"
                    inputMode="numeric"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.75rem' }}>$</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Housing Loan"
                    value={housingLoan}
                    onChange={(e) => setHousingLoan(formatNumberWithCommas(e.target.value))}
                    type="text"
                    inputMode="numeric"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.75rem' }}>$</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Credit Card"
                    value={creditCard}
                    onChange={(e) => setCreditCard(formatNumberWithCommas(e.target.value))}
                    type="text"
                    inputMode="numeric"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.75rem' }}>$</Typography>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Other Loan"
                    value={otherLoan}
                    onChange={(e) => setOtherLoan(formatNumberWithCommas(e.target.value))}
                    type="text"
                    inputMode="numeric"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 0.5, fontSize: '0.75rem' }}>$</Typography>,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Remarks
              </Typography>
              <TextField
                fullWidth
                label="Remarks"
                value={profileRemarks}
                onChange={(e) => setProfileRemarks(e.target.value)}
                multiline
                rows={3}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsProfileDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveProfile}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: '#D97706',
              '&:hover': {
                backgroundColor: '#B86205',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Dialog */}
      <Dialog open={isGroupDialogOpen} onClose={() => setIsGroupDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingGroup ? 'Edit Group' : 'Create Group'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            required
          />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Relationship</InputLabel>
            <Select
              value={groupRelationship}
              label="Relationship"
              onChange={(e) => setGroupRelationship(e.target.value)}
            >
              <MenuItem value="SPOUSE">Married Couple</MenuItem>
              <MenuItem value="INDIVIDUAL">Individual</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Profiles in Group</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleCreateProfileInGroup}
              >
                Create New Profile
              </Button>
            </Box>

            {/* Search field for profiles */}
            <TextField
              fullWidth
              placeholder="Search profiles by name..."
              value={profileSearchTerm}
              onChange={(e) => {
                setProfileSearchTerm(e.target.value);
                setProfilePage(1); // Reset to first page when searching
              }}
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {creatingProfileInGroup && newProfileInGroup && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                  Create New Profile
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Profile Name"
                      value={newProfileInGroup.Name}
                      onChange={(e) =>
                        setNewProfileInGroup({
                          ...newProfileInGroup,
                          Name: e.target.value,
                        })
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Age"
                      value={
                        newProfileInGroup.BirthYear
                          ? getAgeFromBirthYear(newProfileInGroup.BirthYear)
                          : ''
                      }
                      onChange={(e) => {
                        const ageValue = e.target.value.replace(/\D/g, '');
                        const birthYear = ageValue ? getBirthYearFromAge(ageValue) : null;
                        setNewProfileInGroup({
                          ...newProfileInGroup,
                          BirthYear: birthYear,
                        });
                      }}
                      type="number"
                      size="small"
                      helperText={
                        newProfileInGroup.BirthYear
                          ? `Birth Year: ${newProfileInGroup.BirthYear}`
                          : ''
                      }
                      FormHelperTextProps={{
                        sx: { color: 'text.secondary' },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Monthly Income"
                      value={newProfileInGroup.MonthlyIncome != null ? formatNumberWithCommas(String(newProfileInGroup.MonthlyIncome)) : ''}
                      onChange={(e) =>
                        setNewProfileInGroup({
                          ...newProfileInGroup,
                          MonthlyIncome: parseNumberString(e.target.value),
                        })
                      }
                      type="text"
                      inputMode="numeric"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Other Annual Income"
                      value={newProfileInGroup.OtherAnnualIncome != null ? formatNumberWithCommas(String(newProfileInGroup.OtherAnnualIncome)) : ''}
                      onChange={(e) =>
                        setNewProfileInGroup({
                          ...newProfileInGroup,
                          OtherAnnualIncome: parseNumberString(e.target.value),
                        })
                      }
                      type="text"
                      inputMode="numeric"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cash / Saving"
                      value={newProfileInGroup.Saving != null ? formatNumberWithCommas(String(newProfileInGroup.Saving)) : ''}
                      onChange={(e) =>
                        setNewProfileInGroup({
                          ...newProfileInGroup,
                          Saving: parseNumberString(e.target.value),
                        })
                      }
                      type="text"
                      inputMode="numeric"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>$</Typography>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="CPF OA"
                      value={newProfileInGroup.Cpf != null ? formatNumberWithCommas(String(newProfileInGroup.Cpf)) : ''}
                      onChange={(e) =>
                        setNewProfileInGroup({
                          ...newProfileInGroup,
                          Cpf: parseNumberString(e.target.value),
                        })
                      }
                      type="text"
                      inputMode="numeric"
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>$</Typography>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      Monthly Debt Obligations
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Car Loan"
                      value={newProfileInGroup.CarLoan != null ? formatNumberWithCommas(String(newProfileInGroup.CarLoan)) : ''}
                      onChange={(e) =>
                        setNewProfileInGroup({
                          ...newProfileInGroup,
                          CarLoan: parseNumberString(e.target.value),
                        })
                      }
                      type="text"
                      inputMode="numeric"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Housing Loan"
                      value={newProfileInGroup.HousingLoan != null ? formatNumberWithCommas(String(newProfileInGroup.HousingLoan)) : ''}
                      onChange={(e) =>
                        setNewProfileInGroup({
                          ...newProfileInGroup,
                          HousingLoan: parseNumberString(e.target.value),
                        })
                      }
                      type="text"
                      inputMode="numeric"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Credit Card"
                      value={newProfileInGroup.CreditCard != null ? formatNumberWithCommas(String(newProfileInGroup.CreditCard)) : ''}
                      onChange={(e) =>
                        setNewProfileInGroup({
                          ...newProfileInGroup,
                          CreditCard: parseNumberString(e.target.value),
                        })
                      }
                      type="text"
                      inputMode="numeric"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Other Loan"
                      value={newProfileInGroup.OtherLoan != null ? formatNumberWithCommas(String(newProfileInGroup.OtherLoan)) : ''}
                      onChange={(e) =>
                        setNewProfileInGroup({
                          ...newProfileInGroup,
                          OtherLoan: parseNumberString(e.target.value),
                        })
                      }
                      type="text"
                      inputMode="numeric"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      size="small"
                      onClick={() => {
                        setCreatingProfileInGroup(false);
                        setNewProfileInGroup(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {profiles.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No profiles available. Create a profile first or create one above.
              </Typography>
            ) : (() => {
              // Filter profiles based on search term
              const filteredProfiles = profiles.filter((profile) =>
                profile.Name.toLowerCase().includes(profileSearchTerm.toLowerCase())
              );

              if (filteredProfiles.length === 0 && profileSearchTerm) {
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No profiles found matching "{profileSearchTerm}"
                  </Typography>
                );
              }

              const currentYear = new Date().getFullYear();
              
              // Calculate pagination
              const totalPages = Math.ceil(filteredProfiles.length / profilesPerPage);
              const startIndex = (profilePage - 1) * profilesPerPage;
              const endIndex = startIndex + profilesPerPage;
              const paginatedProfiles = filteredProfiles.slice(startIndex, endIndex);
              
              return (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    {paginatedProfiles.map((profile) => {
                      const isSelected = selectedProfileIds.includes(profile.Id);
                      const age = profile.BirthYear ? currentYear - profile.BirthYear : null;
                      
                      return (
                        <Card
                          key={profile.Id}
                          sx={{
                            border: isSelected ? '2px solid #D97706' : '1px solid',
                            borderColor: isSelected ? '#D97706' : 'divider',
                            backgroundColor: isSelected ? 'rgba(217, 119, 6, 0.05)' : 'background.paper',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            mb: 1,
                            '&:hover': {
                              boxShadow: 2,
                              borderColor: isSelected ? '#D97706' : 'primary.main',
                              backgroundColor: isSelected ? 'rgba(217, 119, 6, 0.08)' : 'action.hover',
                            },
                          }}
                          onClick={() => handleToggleProfileInGroup(profile.Id)}
                        >
                          <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap' }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleToggleProfileInGroup(profile.Id)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ 
                                  color: isSelected ? '#D97706' : 'default',
                                  '&.Mui-checked': {
                                    color: '#D97706',
                                  },
                                  flexShrink: 0,
                                }}
                              />
                              <PersonIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: isSelected ? '#D97706' : 'text.primary',
                                  minWidth: 100,
                                  flexShrink: 0,
                                }}
                              >
                                {profile.Name}
                              </Typography>
                              {isSelected && (
                                <Chip
                                  label="Selected"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.6rem',
                                    backgroundColor: '#D97706',
                                    color: 'white',
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, ml: 1 }}>
                                {age !== null && (
                                  <Typography variant="body2" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                    <Typography component="span" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                      Age:{' '}
                                    </Typography>
                                    <Typography component="span" sx={{ fontWeight: 600 }}>
                                      {age} years
                                    </Typography>
                                  </Typography>
                                )}
                                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                  <Typography component="span" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    Assessable:{' '}
                                  </Typography>
                                  <Typography component="span" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(
                                      calculateAssessableIncome(
                                        profile.MonthlyIncome ? String(profile.MonthlyIncome) : '0',
                                        profile.OtherAnnualIncome ? String(profile.OtherAnnualIncome) : '0',
                                        profile.SelfEmployedInd
                                      )
                                    )}
                                  </Typography>
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                  
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={totalPages}
                        page={profilePage}
                        onChange={(event, value) => setProfilePage(value)}
                        color="primary"
                        size="small"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                  
                  {filteredProfiles.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredProfiles.length)} of {filteredProfiles.length} profiles
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Box>

          <TextField
            fullWidth
            label="Remarks"
            value={groupRemarks}
            onChange={(e) => setGroupRemarks(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsGroupDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveGroup}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: '#D97706',
              '&:hover': {
                backgroundColor: '#B86205',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileManager;
